import React from 'react';
import { PROFILE_KEYS } from '../profile/productProfiles';
import { pricePosition, profitLoss, violations, suggestionAction, mapValue, mrpValue } from './productSignals';
import SuggestionAction from './SuggestionAction';
import AllowedBand from './AllowedBand';
import PriceBand from './PriceBand';

// Red -> green gradient by price position (most expensive -> cheapest).
const POSITION_LABEL = {
  highest:{ key: 'product.myprice_highest', cls: 'pux-chip-red' },
  high:   { key: 'product.myprice_avg_higher', cls: 'pux-chip-orange' },
  average:{ key: 'product.myprice_average', cls: 'pux-chip-yellow' },
  low:    { key: 'product.myprice_avg_lower', cls: 'pux-chip-cyan' },
  same:   { key: 'product.myprice_same', cls: 'pux-chip-blue' },
  lowest: { key: 'product.myprice_lowest', cls: 'pux-chip-green' },
};

export default function StatusCell({ row, profileKey, t, templateNameById = {} }) {
  const chips = [];

  // Applied dynamic-pricing template tag. The summary only carries the id, so we
  // resolve the name from the lookup map; if the id is missing (e.g. template
  // deleted) we fall back to a generic label instead of showing a raw id.
  if (row.pricing_template_id) {
    const templateName = templateNameById[row.pricing_template_id];
    const templateLabel = templateName
      ? t('pricing_template.tag_label', { name: templateName })
      : t('pricing_template.tag_generic');
    chips.push(
      <span key="template" className="pux-chip pux-chip-blue"><i className="pi pi-bolt" /> {templateLabel}</span>
    );
  }

  // MARKET and MARKETPLACE profiles get no verdict chips and no suggestion action.
  if (profileKey === PROFILE_KEYS.BRAND) {
    const v = violations(row);
    if (v.map) chips.push(<span key="map" className="pux-chip pux-chip-red"><i className="pi pi-ban" /> {t('price_violation.map_abbreviation')}</span>);
    if (v.mrp) chips.push(<span key="mrp" className="pux-chip pux-chip-red"><i className="pi pi-ban" /> {t('price_violation.mrp_abbreviation')}</span>);
    if (row.unauthorized_count > 0) chips.push(<span key="ua" className="pux-chip pux-chip-violet"><i className="pi pi-user-minus" /> {t('product.unauthorized_count', { count: row.unauthorized_count })}</span>);
    if (!chips.length) chips.push(<span key="ok" className="pux-chip pux-chip-green">{t('product.compliant')}</span>);
  } else if (profileKey === PROFILE_KEYS.DROPSHIPPING) {
    const pl = profitLoss(row);
    if (pl) chips.push(<span key="pl" className={`pux-chip ${pl.loss ? 'pux-chip-red' : 'pux-chip-green'}`}>{t(pl.loss ? 'product.loss' : 'product.profit')}</span>);
    const pos = pricePosition(row);
    if (pos) chips.push(<span key="pos" className={`pux-chip ${POSITION_LABEL[pos.level].cls}`}>{t(POSITION_LABEL[pos.level].key)}</span>);
  } else if (profileKey === PROFILE_KEYS.COMPETITIVE) {
    const pos = pricePosition(row);
    if (pos) chips.push(<span key="pos" className={`pux-chip ${POSITION_LABEL[pos.level].cls}`}>{t(POSITION_LABEL[pos.level].key)}</span>);
  }

  // Stock line: invalid link takes priority, else in-stock when stock > 0
  let stockNode = null;
  if (row.is_url_deactivated) {
    stockNode = (
      <span style={{ fontSize: '.72rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
        <span className="pux-dot" style={{ background: '#9ca3af' }} />
        {t('product.invalid_link')}
      </span>
    );
  } else if (row.stock != null) {
    const inStock = row.stock > 0;
    stockNode = (
      <span style={{ fontSize: '.72rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
        <span className="pux-dot" style={{ background: inStock ? '#16a34a' : '#dc2626' }} />
        {t(inStock ? 'product.stock_in' : 'product.stock_out')}
      </span>
    );
  }
  const currency = row.currency || (row.violation_strategy && row.violation_strategy.currency) || '';
  // formatCurrency returns JSX (superscript currency); for a plain-string tooltip
  // we format the number ourselves and append the currency code.
  const curStr = typeof currency === 'string' ? currency.toUpperCase() : '';
  const targetStr = row.target_price != null
    ? Number(row.target_price).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;
  const tip = (targetStr != null)
    ? `${t('product.target_price')}: ${targetStr} ${curStr}`.trim()
    : t('product.suggested_price_tooltip');
  const showSuggestion = (profileKey === PROFILE_KEYS.COMPETITIVE || profileKey === PROFILE_KEYS.DROPSHIPPING) && suggestionAction(row);

  return (
    <div className="pux-status">
      {chips}
      {profileKey === PROFILE_KEYS.BRAND && mapValue(row) != null && mrpValue(row) != null && (
        <div style={{ width: '100%', maxWidth: '12em' }}>
          <AllowedBand map={mapValue(row)} mrp={mrpValue(row)}
                       worstBelow={row.competitorsMinPrice} worstAbove={row.competitorsMaxPrice} />
        </div>
      )}
      {(profileKey === PROFILE_KEYS.COMPETITIVE || profileKey === PROFILE_KEYS.DROPSHIPPING)
        && row.price > 0 && row.competitorsMinPrice != null && row.competitorsMaxPrice != null && (
        <div style={{ width: '100%', maxWidth: '12em' }}>
          <PriceBand price={row.price} min={row.competitorsMinPrice} max={row.competitorsMaxPrice} />
        </div>
      )}
      {stockNode}
      {showSuggestion && (
        <span className="pux-sugg-tip" data-pr-tooltip={tip} data-pr-position="top" style={{ fontSize: '.74rem', cursor: 'help' }}>
          <SuggestionAction row={row} currency={currency} />
        </span>
      )}
    </div>
  );
}
